<template>
  <div class="date-time-picker-wrap">
    <!-- START : Date Picker Wrap -->
    <div v-if="canDisplayDatePicker" class="date-time-element date-picker-wrap position-relative">
      <!-- START : Date Picker Input -->
      <input
        :id="dateInputId"
        type="text"
        class="form-control bg-light"
        v-model="$v.form.date.$model"
        :class="{ 'is-invalid': $v.form.date.$error }"
        :placeholder="datePlaceholder"
        @input="onDatePickerInput"
        :aria-label="datePickerAriaLabel"
        :autocomplete="isBirthDate && 'bday'"
      />
      <!-- END : Date Picker Input -->
      <!-- START : Date Picker -->
      <v-date-picker
        :select-attribute="attribute"
        :drag-attribute="attribute"
        :min-date="minDate"
        :max-date="maxDate"
        :from-date="fromDate"
        mode="date"
        :value="$v.form.date.$model"
        @dayclick="onDayClicked"
        @popoverDidShow="setDatePickerPopOverState(true)"
        @popoverDidHide="setDatePickerPopOverState(false)"
      >
        <template v-slot="{ togglePopover }">
          <!-- START : Date Picker Btn -->
          <button
            @click="togglePopover()"
            type="button"
            class="date-picker-btn unstyled-btn"
            ref="datePickerBtn"
            :aria-label="$t('datePicker')"
          >
            <font-awesome-icon icon="calendar-days" size="1x" />
          </button>
          <!-- END : Date Picker Btn -->
        </template>
      </v-date-picker>
      <!-- END : Date Picker -->
    </div>
    <!-- END : Date Picker Wrap -->

    <!-- START : Time Picker Wrap -->
    <div v-if="canDisplayTimePicker" class="date-time-element time-picker-wrap position-relative">
      <!-- START : Time Picker Input -->
      <input
        type="text"
        class="form-control bg-light"
        :id="timeInputId"
        v-model="$v.form.time.$model"
        :class="{ 'is-invalid': $v.form.time.$error }"
        :placeholder="timePlaceholder"
        @input="onTimePickerInput"
        :aria-label="timePickerAriaLabel"
      />
      <!-- END : Time Picker Input -->
      <!-- START : Date Picker -->
      <v-date-picker
        :select-attribute="attribute"
        :drag-attribute="attribute"
        mode="time"
        :minute-increment="minuteIncrement"
        :value="dateTimeValue"
        :from-date="fromDate"
        @input="onTimeSelection"
        @popoverDidShow="setTimePickerPopOverState(true)"
        @popoverDidHide="setTimePickerPopOverState(false)"
      >
        <template v-slot="{ togglePopover }">
          <!-- START : Date Picker Btn -->
          <button
            @click="togglePopover()"
            type="button"
            class="time-picker-btn unstyled-btn"
            ref="timePickerBtn"
            :aria-label="$t('timePicker')"
          >
            <font-awesome-icon icon="clock" size="1x" />
          </button>
          <!-- END : Date Picker Btn -->
        </template>
      </v-date-picker>
      <!-- END : Date Picker -->
    </div>
    <!-- END : Time Picker Wrap -->
  </div>
</template>
<script>
import { Locale } from 'v-calendar';

import {
  DATE_PICKER_CONFIG_BY_FORMAT,
  FORMAT,
  MINUTE_INCREMENT,
  MODE,
} from '@/constants/DatePickerConstants';
import { helpers, required } from 'vuelidate/lib/validators';
import { checkDateTimeFormat } from '@/utils/formatters';
import { format, isSameDay } from 'date-fns';

const locale = new Locale();

/**
 * Validation method for minimum Date based on params
 * @param {Date} minMaxDate - min or max date
 * @param {String} dateFormat - date format to validate
 * @param {Boolean} isPartialDate - is partial date/time input or both date & time input
 * @param {Boolean} isMinDateCheck - true - minDate to validate or false - maxDate to validate
 */
const minMaxDateValidator = (minMaxDate, dateFormat, isPartialDate, isMinDateCheck) => {
  const type = isMinDateCheck ? 'minimumDate' : 'maximumDate';
  const minMaxParamKey = isMinDateCheck ? 'min' : 'max';
  return helpers.withParams({ type, [minMaxParamKey]: minMaxDate }, value => {
    let followsMinMaxDate = true;
    const dateString = isPartialDate ? value : `${value.date} ${value.time}`;
    const { invalidDate, date } = checkDateTimeFormat(dateFormat, dateString);
    if (!invalidDate) {
      const dateMs = date.getTime();
      const minMaxDateMs = minMaxDate ? minMaxDate.getTime() : null;
      if (minMaxDateMs && !(isPartialDate && isSameDay(minMaxDate, date))) {
        followsMinMaxDate = !(isMinDateCheck ? dateMs < minMaxDateMs : dateMs > minMaxDateMs);
      }
    }
    return followsMinMaxDate;
  });
};

/**
 * Validation method to check invalid date based on params
 * @param {String} dateFormat - date format to validate
 * @param {Boolean} isPartialDate - is partial date/time input or both date & time input
 */
const isValidDateValidator = (dateFormat, isPartialDate = false, requiredDate = true) => {
  return helpers.withParams(
    {
      type: 'validDate',
      dateFormat,
      isPartialDate,
    },
    value => {
      const dateString = isPartialDate ? value : `${value.date} ${value.time}`;
      const { invalidDate } = checkDateTimeFormat(dateFormat, dateString);
      if (!dateString && !requiredDate) return true;
      return !invalidDate;
    },
  );
};

export default {
  name: 'WCDateTimePicker',
  emits: ['onChange', 'onValidation'],
  props: {
    /* Time picker Input Id */
    dateInputId: {
      type: String,
      default: 'date-picker',
    },
    /* Time picker Input Id */
    timeInputId: {
      type: String,
      default: 'time-picker',
    },
    /* Date picker placeholder */
    datePlaceholder: {
      type: String,
      default: 'Select Date',
    },
    /* Time picker placeholder */
    timePlaceholder: {
      type: String,
      default: 'Select Time',
    },
    /* Date Input format */
    dateFormat: {
      type: String,
      default: FORMAT.DATE_DEFAULT,
    },
    /* Time Input format */
    timeFormat: {
      type: String,
      default: FORMAT.TIME_DEFAULT,
    },
    /* Initial date picker selected value */
    initialValue: {
      type: [Date, String],
      default: new Date(),
    },
    /* Date picker min date configuration */
    minDate: {
      type: Date,
      default: null,
    },
    /* Date picker max date configuration */
    maxDate: {
      type: Date,
      default: null,
    },
    /* Date picker modes */
    mode: {
      type: String,
      default: MODE.DATE,
    },
    /* Minute  increment steps in time picker */
    minuteIncrement: {
      type: Number,
      default: MINUTE_INCREMENT.BY_FIFTEEN,
    },
    /* Initial from date to be displayed in calendar if initialDate is not passed */
    fromDate: {
      type: Date,
      default: null,
    },
    /* Aria label for date picker */
    datePickerAriaLabel: {
      type: String,
    },
    /* Aria label for time picker */
    timePickerAriaLabel: {
      type: String,
    },
    /* Boolean to determine if date is required or not */
    requiredDate: {
      type: Boolean,
      default: true,
    },
    /* Boolean to determine if date is the user's birthday date */
    isBirthDate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const { textColor, bgColor, bgLightColor } = this.getThemeColors();
    return {
      /* Date picker Attributes */
      attribute: {
        // Date picker Highlight Attributes
        highlight: {
          start: {
            style: {
              backgroundColor: bgColor,
            },
            contentStyle: {
              color: textColor,
            },
          },
          base: {
            style: {
              backgroundColor: bgLightColor,
            },
          },
          end: {
            style: {
              backgroundColor: bgColor,
            },
            contentStyle: {
              color: textColor,
            },
          },
        },
      },
      /* Date/Time Picker Popover */
      isDatePickerPopOverOpen: false,
      isTimePickerPopOverOpen: false,
      /* Date/Time form value */
      form: {
        date: '',
        time: '',
      },
    };
  },

  /**
   * Vuelidate Validation specifications
   * 1. form.date validations
   * 2. form.time validations
   * 3. form validations
   */
  validations() {
    return {
      form: {
        /**
         * Date validations - required, minimumDate, maximumDate
         */
        date: this.dateValidator,
        /**
         * Time validations - required, validDate
         */
        time: {
          required,
          validDate: isValidDateValidator(
            DATE_PICKER_CONFIG_BY_FORMAT[this.timeFormat].checkFormat,
            true,
          ),
        },
        required,
        /**
         * Date & Time validations - validDate
         */
        validDate: isValidDateValidator(
          `${DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat} ${
            DATE_PICKER_CONFIG_BY_FORMAT[this.timeFormat].checkFormat
          }`,
          false,
          this.requiredDate,
        ),
        /**
         * Date & Time validations - minimumDate
         */
        minimumDate: minMaxDateValidator(
          this.minDate,
          `${DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat} ${
            DATE_PICKER_CONFIG_BY_FORMAT[this.timeFormat].checkFormat
          }`,
          false,
          true,
        ),
        /**
         * Date & Time validations - maximumDate
         */
        maximumDate: minMaxDateValidator(
          this.maxDate,
          `${DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat} ${
            DATE_PICKER_CONFIG_BY_FORMAT[this.timeFormat].checkFormat
          }`,
          false,
          false,
        ),
      },
    };
  },
  created() {
    this.setInitialValue();
  },
  watch: {
    /**
     * Set date picker value based on initial value changes
     */
    initialValue() {
      this.setInitialValue();
    },
  },
  computed: {
    /**
     * Method to check if the datepicker can be displayed based on mode
     * @return {boolean} canDisplayDatePicker
     */
    canDisplayDatePicker() {
      return [MODE.DATE, MODE.DATE_TIME].includes(this.mode);
    },
    /**
     * Method to check if the timePicker can be displayed based on mode
     * @return {boolean} canDisplayTimePicker
     */
    canDisplayTimePicker() {
      return [MODE.TIME, MODE.DATE_TIME].includes(this.mode);
    },
    /**
     * Method to get parsed selected date & selected time string as date object
     * @return {Date} dateTimeValue
     */
    dateTimeValue() {
      return locale.parse(
        `${this.form.time} ${this.form.date}`,
        `${this.dateFormat} ${this.timeFormat}`,
      );
    },
    /**
     * Dynamic date validator based on required property
     */
    dateValidator() {
      let validator = {};
      const defaultValidator = {
        minimumDate: minMaxDateValidator(
          this.minDate,
          DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat,
          true,
          true,
        ),
        maximumDate: minMaxDateValidator(
          this.maxDate,
          DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat,
          true,
          false,
        ),
      };
      if (this.requiredDate) {
        validator = {
          required,
          validDate: isValidDateValidator(
            DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat,
            true,
          ),
          ...defaultValidator,
        };
      } else {
        validator = {
          validDate: isValidDateValidator(
            DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat,
            true,
            false,
          ),
          ...defaultValidator,
        };
      }
      return validator;
    },
  },
  methods: {
    /**
     * Method to set initial date value
     */
    setInitialValue() {
      let initialDate = this.initialValue;
      const dateFormat = this.getDateFormatByMode(this.mode, this.dateFormat, this.timeFormat);
      if (typeof initialDate === 'string') {
        initialDate = checkDateTimeFormat(dateFormat, initialDate).date;
      }
      if (initialDate) {
        this.setDate(initialDate);
        this.setTime(initialDate);
        this.emitDate();
      }
    },
    /**
     * Method to set date value on form
     * @param {Date} - date
     */
    setDate(date) {
      const dateString = format(date, DATE_PICKER_CONFIG_BY_FORMAT[this.dateFormat].checkFormat);
      this.$v.form.date.$touch();
      this.form.date = dateString;
    },
    /**
     * Method to set time value on form
     * @param {Date} - time
     */
    setTime(time) {
      const timeString = format(time, DATE_PICKER_CONFIG_BY_FORMAT[this.timeFormat].checkFormat);
      this.$v.form.time.$touch();
      this.form.time = timeString;
    },
    /**
     * Method to compute and return theme colors for date picker
     */
    getThemeColors() {
      const elementRef = getComputedStyle(document.documentElement);
      const primaryColor = elementRef.getPropertyValue('--primary') || '#0d6efd';
      const primaryContrast = elementRef.getPropertyValue('--primary-contrast') || '#fff';
      const primaryLighter5 = elementRef.getPropertyValue('--primary-lighter-5') || '#0d6efd';
      return { textColor: primaryContrast, bgColor: primaryColor, bgLightColor: primaryLighter5 };
    },
    /**
     * Method to set/unset date picker popover state - isDatePickerPopOverOpen
     * Also on open add aria labels to left and right arrow for month
     * @param {boolean} - isOpen
     */
    setDatePickerPopOverState(isOpen) {
      this.isDatePickerPopOverOpen = isOpen;
      if (isOpen) {
        const leftArrow = this.$el.getElementsByClassName('vc-arrow is-left')[0];
        const rightArrow = this.$el.getElementsByClassName('vc-arrow is-right')[0];
        leftArrow?.setAttribute('aria-label', this.$t('goToPreviousMonth'));
        rightArrow?.setAttribute('aria-label', this.$t('goToNextMonth'));
      }
    },
    /**
     * Method to set/unset time picker popover state - isTimePickerPopOverOpen
     * @param {boolean} - isOpen
     */
    setTimePickerPopOverState(isOpen) {
      this.isTimePickerPopOverOpen = isOpen;
      if (isOpen) {
        const hourSelect = this.$el.getElementsByClassName('vc-select')[0]?.childNodes[0];
        const minSelect = this.$el.getElementsByClassName('vc-select')[1]?.childNodes[0];
        hourSelect?.setAttribute('aria-label', this.$t('selectHours'));
        minSelect?.setAttribute('aria-label', this.$t('selectMinutes'));
      }
    },
    /**
     * Method invoked on date picker input to show date popover on input
     */
    onDatePickerInput() {
      if (!this.isDatePickerPopOverOpen) {
        // Trigger click event to open the date picker
        this.$refs.datePickerBtn.click();
      }
      this.emitDate();
    },
    /**
     * Method invoked on time picker input to show time popover on input
     */
    onTimePickerInput() {
      if (!this.isTimePickerPopOverOpen) {
        // Trigger click event to open the time picker
        this.$refs.timePickerBtn.click();
      }
      this.emitDate();
    },
    /**
     * Method invoked on date picker popover day selection to set date input form element and emit events
     */
    onDayClicked(event) {
      this.setDate(event.date);
      this.emitDate();
    },
    /**
     * Method invoked on time picker popover time selection to set time input form element and emit events
     */
    onTimeSelection(date) {
      this.setTime(date);
      this.emitDate();
    },

    /**
     * Method to emit date & time values and also the validation object
     */
    emitDate() {
      const dateFormat = this.getDateFormatByMode(this.mode, this.dateFormat, this.timeFormat);
      let dateString = `${this.form.date} ${this.form.time}`;
      if (this.mode === MODE.DATE) {
        dateString = this.form.date;
      } else if (this.mode === MODE.TIME) {
        dateString = this.form.time;
      }
      const { date } = checkDateTimeFormat(dateFormat, dateString);
      this.$emit('onChange', { dateString, date });
      this.$emit('onValidation', this.$v.form);
    },

    /**
     * Method to get date format based on mode
     * @param {String} mode - date picker mode
     * @param {String} dateFormat - date format which is used from date picker date format
     * @param {String} timeFormat - time format which is used from date picker time format
     * @return {String} date or time format based on mode
     */
    getDateFormatByMode(mode, dateFormat, timeFormat) {
      const config = {
        [MODE.DATE]: DATE_PICKER_CONFIG_BY_FORMAT[dateFormat].checkFormat,
        [MODE.TIME]: DATE_PICKER_CONFIG_BY_FORMAT[timeFormat].checkFormat,
        [MODE.DATE_TIME]: `${DATE_PICKER_CONFIG_BY_FORMAT[dateFormat].checkFormat} ${DATE_PICKER_CONFIG_BY_FORMAT[timeFormat].checkFormat}`,
      };
      return config[mode];
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

/*---- START : Date Time Picker Styles ----*/
.date-time-picker-wrap {
  display: flex;
  box-sizing: border-box;
  display: flex;

  input {
    border: 1px solid var(--dark, $dark);
  }

  .date-time-element {
    flex-grow: 1;
    margin-right: rem(10px);

    &:last-child {
      margin-right: 0;
    }
  }

  ::v-deep .vc-popover-content-wrapper[data-popper-placement='top-start'] {
    bottom: 100% !important;
  }

  /*---- START : Date Time Picker Button Styles ----*/
  .date-picker-btn,
  .time-picker-btn {
    position: absolute;
    right: 0;
    bottom: 50%;
    transform: translate(0, 50%);
    padding: $pad-1 $pad-2;
  }
  /*---- END : Date Time Picker Button Styles ----*/

  /*---- START : Invalid Form Control Styles ----*/
  .was-validated .form-control:invalid,
  .form-control.is-invalid {
    background-position: right 2rem center;
  }
  /*---- END : Invalid Form Control Styles ----*/
}
/*---- END : Date Time Picker Styles ----*/

/* ---- START : V-CALENDAR STYLES */
::v-deep .vc-weekday {
  color: var(--primary, $primary);
}
::v-deep .vc-date .vc-year {
  color: var(--primary, $primary);
}
/* ---- END : V-CALENDAR STYLES */
</style>
